import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "./ui/command";
import {
  LayoutDashboard, Users, Briefcase, CreditCard, Wallet, FileText,
  CalendarClock, FolderKanban, UserCog, ShieldCheck, ScrollText,
  Banknote, Wrench, Settings as SettingsIcon, UsersRound, Search,
  ArrowRight, KeyRound, Star,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { api } from "../lib/api";

const MODULE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: ["dashboard", "view"] },
  { to: "/customers", label: "Clientes", icon: Users, perm: ["customers", "view"] },
  { to: "/services", label: "Serviços", icon: Briefcase, perm: ["services", "view"] },
  { to: "/payments", label: "Pagamentos", icon: CreditCard, perm: ["payments", "view"] },
  { to: "/balance", label: "Saldo", icon: Wallet, perm: ["balance", "view"] },
  { to: "/documents", label: "Documentos", icon: FileText, perm: ["documents", "view"] },
  { to: "/appointments", label: "Agendamento", icon: CalendarClock, perm: ["appointments", "view"] },
  { to: "/processes", label: "Acompanhamento de Processos", icon: FolderKanban, perm: ["processes", "view"] },
  { to: "/hr/employees", label: "RH · Funcionários", icon: UserCog, perm: ["employees", "view"] },
  { to: "/payroll/dashboard", label: "Folha de Pagamento", icon: Banknote, perm: ["payroll", "view"] },
  { to: "/users", label: "Usuários", icon: UsersRound, perm: ["users", "view"] },
  { to: "/settings/roles", label: "Papéis e Permissões", icon: ShieldCheck, perm: ["roles", "view"] },
  { to: "/settings/audit", label: "Central de Auditoria", icon: ScrollText, perm: ["audit", "view"] },
  { to: "/settings", label: "Configurações", icon: SettingsIcon, perm: ["settings", "view"] },
];

const FAV_KEY_PREFIX = "jms.favorites.v1";

/**
 * AP-021 Sprint 2 · Global Command Palette (Ctrl/Cmd + K)
 * - Fuzzy search over modules + backend entities (customers/services/payments)
 * - Keyboard shortcuts: ⌘K / Ctrl+K to open, Enter to navigate, Escape to close
 * - Actions to (un)favorite modules — persisted in localStorage per user
 */
export default function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ customers: [], services: [], payments: [] });
  const [favorites, setFavorites] = useState([]);
  const { user } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();
  const isMSA = user?.role === "master_super_admin";
  const favKey = `${FAV_KEY_PREFIX}.${user?.id || "anon"}`;

  useEffect(() => {
    try {
      const raw = localStorage.getItem(favKey);
      setFavorites(raw ? JSON.parse(raw) : []);
    } catch { setFavorites([]); }
  }, [favKey]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    // Broadcast helper so any button can open the palette
    window.__openCmdK = () => setOpen(true);
    return () => {
      document.removeEventListener("keydown", onKey);
      delete window.__openCmdK;
    };
  }, []);

  useEffect(() => {
    if (!open || !q || q.length < 2) {
      setResults({ customers: [], services: [], payments: [] });
      return;
    }
    const to = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults({
          customers: data.customers || [],
          services: data.services || [],
          payments: data.payments || [],
        });
      } catch { setResults({ customers: [], services: [], payments: [] }); }
    }, 220);
    return () => clearTimeout(to);
  }, [q, open]);

  const modules = useMemo(() => {
    const list = MODULE_NAV.filter((m) => !m.perm || can(m.perm[0], m.perm[1]));
    if (isMSA) list.push({ to: "/settings/system-tools", label: "Ferramentas de Sistema", icon: Wrench });
    return list;
  }, [can, isMSA]);

  const go = (to) => { setOpen(false); setQ(""); nav(to); };

  const toggleFavorite = (to) => {
    // Defer the state update + broadcast so React doesn't warn about
    // updating another component (Sidebar) during our render pass.
    setTimeout(() => {
      setFavorites((cur) => {
        const next = cur.includes(to) ? cur.filter((x) => x !== to) : [...cur, to];
        try { localStorage.setItem(favKey, JSON.stringify(next)); } catch {}
        window.dispatchEvent(new CustomEvent("jms:favorites-changed", { detail: next }));
        return next;
      });
    }, 0);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="border-b border-border" data-testid="cmdk-dialog">
        <CommandInput
          value={q} onValueChange={setQ}
          placeholder="Buscar módulos, clientes, serviços, pagamentos…  (⌘K / Ctrl+K)"
          data-testid="cmdk-input"
        />
      </div>
      <CommandList>
        <CommandEmpty>Nenhum resultado.</CommandEmpty>

        <CommandGroup heading="Módulos" data-testid="cmdk-modules-group">
          {modules.map((m) => (
            <CommandItem key={m.to} value={`mod ${m.label}`}
                         onSelect={() => go(m.to)}
                         data-testid={`cmdk-mod-${m.to.replace(/\//g, "-")}`}>
              <m.icon className="h-4 w-4 mr-2 text-primary" />
              <span className="flex-1">{m.label}</span>
              <span className="text-[11px] text-muted-foreground font-mono mr-2">{m.to}</span>
              <button
                type="button"
                aria-label={favorites.includes(m.to) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(m.to); }}
                onMouseDown={(e) => e.preventDefault()}
                className={`p-1 rounded hover:bg-muted transition-colors ${favorites.includes(m.to) ? "text-primary" : "text-muted-foreground"}`}
                data-testid={`cmdk-fav-${m.to.replace(/\//g, "-")}`}
              >
                <Star className="h-3.5 w-3.5" fill={favorites.includes(m.to) ? "currentColor" : "none"} />
              </button>
            </CommandItem>
          ))}
        </CommandGroup>

        {results.customers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Clientes" data-testid="cmdk-customers-group">
              {results.customers.slice(0, 8).map((c) => (
                <CommandItem key={`c-${c.id}`} value={`cli ${c.full_name} ${c.email || ""} ${c.phone || ""}`}
                             onSelect={() => go(`/customers/${c.id}`)}>
                  <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="flex-1 truncate">{c.full_name}</span>
                  <span className="text-xs text-muted-foreground truncate ml-2">{c.email || c.phone || "—"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.services.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Serviços" data-testid="cmdk-services-group">
              {results.services.slice(0, 8).map((s) => (
                <CommandItem key={`s-${s.id}`} value={`srv ${s.name}`} onSelect={() => go("/services")}>
                  <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="flex-1 truncate">{s.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.payments.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Pagamentos" data-testid="cmdk-payments-group">
              {results.payments.slice(0, 8).map((p) => (
                <CommandItem key={`p-${p.id}`} value={`pay ${p.customer_name} ${p.service_name}`} onSelect={() => go("/payments")}>
                  <CreditCard className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="flex-1 truncate">{p.customer_name} · {p.service_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Ações rápidas" data-testid="cmdk-actions-group">
          <CommandItem value="settings profile" onSelect={() => go("/settings")}>
            <SettingsIcon className="h-4 w-4 mr-2" /> Abrir Configurações
            <span className="ml-auto text-[10px] text-muted-foreground">Perfil, Empresa</span>
          </CommandItem>
          <CommandItem value="change password" onSelect={() => go("/change-password")}>
            <KeyRound className="h-4 w-4 mr-2" /> Alterar senha
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between">
        <span>↑↓ navegar · <b>Enter</b> abrir · <b>Esc</b> fechar</span>
        <span className="tabular-nums">⌘K / Ctrl+K</span>
      </div>
    </CommandDialog>
  );
}
